import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useInviteUser } from "../../hooks/mutation/create/useInviteUser";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";
import { useUsers } from "../../hooks/query/useUsers";

const schema = z.object({
  email: z.string().trim().email("Valid email is required"),
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  role: z.enum(["admin"]),
});

type FormValues = z.infer<typeof schema>;

const normalizeEmail = (value: string): string => {
  return value.trim().toLowerCase();
};

const InviteUserDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
}> = ({ onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { mutateAsync: inviteUser, isPending: isInviting } = useInviteUser();
  const { data: users = [] } = useUsers();
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      role: "admin",
    },
    mode: "onBlur",
  });

  const onSubmit = async (values: FormValues) => {
    try {
      setSubmitError(null);

      const normalizedEmail = normalizeEmail(values.email);
      const alreadyInvited = users.some((user) => {
        return normalizeEmail(user.email) === normalizedEmail;
      });

      if (alreadyInvited) {
        onDialogClosed({
          message: "User already present in the list.",
          severity: "info",
        });
        return;
      }

      await inviteUser({
        email: normalizedEmail,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        role: values.role,
      });
      reset({ email: "", firstName: "", lastName: "", role: "admin" });
      onDialogClosed({
        message: "Invitation sent!",
        severity: "success",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isInviting) {
      return;
    }
    setSubmitError(null);
    reset({ email: "", firstName: "", lastName: "", role: "admin" });
    onDialogClosed();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title="Invite User"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box component="form" id="invite-user-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Invite a new owner by email address.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              <FormControl>
                <FormLabel>First Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. John"
                  error={!!errors.firstName}
                  {...register("firstName", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.firstName}>
                  {errors.firstName?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Last Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Doe"
                  error={!!errors.lastName}
                  {...register("lastName", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.lastName}>
                  {errors.lastName?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. owner@company.com"
                  error={!!errors.email}
                  {...register("email", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.email}>
                  {errors.email?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Role</FormLabel>
                <TextField
                  size="small"
                  select
                  error={!!errors.role}
                  {...register("role", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                >
                  <MenuItem value="admin">admin</MenuItem>
                </TextField>
                <FormHelperText error={!!errors.role}>
                  {errors.role?.message ?? " "}
                </FormHelperText>
              </FormControl>

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText="Invite"
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isInviting}
          disabled={isInviting}
        />
      </Box>
    </Dialog>
  );
};

export default InviteUserDialog;
